import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { usePaginatedApi } from '../hooks/useApi';
import { productsAPI, clientsAPI, trucksAPI, portsAPI, weighbridgesAPI } from '../services/api';
import { formatDateTime, formatWeight } from '../utils/helpers';
import { getApiErrorMessage } from '../utils/apiErrorMessages';
import { useToast } from '../contexts/ToastContext';
import Modal from './Modal';
import Button from './Button';
import Input from './Input';
import Table from './Table';
import { 
  TruckIcon, 
  BuildingOfficeIcon, 
  MapPinIcon, 
  ScaleIcon,
  CalendarIcon,
  FunnelIcon,
  DocumentArrowDownIcon
} from '@heroicons/react/24/outline';

const ProductLoadsModal = ({ isOpen, onClose, product }) => {
  const { t } = useTranslation();
  const toast = useToast();
  const [filters, setFilters] = useState({
    clientId: '',
    truckId: '',
    portId: '',
    weighbridgeId: '',
    startDate: '',
    endDate: '',
    transactionType: '',
    status: ''
  });
  const [showFilters, setShowFilters] = useState(true);

  // Fetch product loads with pagination
  const [loads, setLoads] = useState([]);
  const [pagination, setPagination] = useState({
    total: 0,
    page: 1,
    limit: 20,
    totalPages: 0,
  });
  const [loading, setLoading] = useState(false);
  
  // Create a ref to store current filters to avoid stale closure
  const filtersRef = useRef(filters);
  useEffect(() => {
    filtersRef.current = filters;
  }, [filters]);

  const fetchLoads = useCallback(async (paramOverrides = {}) => {
    if (!product?.id) return;
    
    try {
      setLoading(true);
      const mergedParams = { ...filtersRef.current, ...paramOverrides };
      const response = await productsAPI.getLoads(product.id, mergedParams);
      
      setLoads(response.data.loads || []);
      setPagination(response.data.pagination || {});
    } catch (error) {
      console.error('Error fetching product loads:', error);
      setLoads([]);
      setPagination({ total: 0, page: 1, limit: 20, totalPages: 0 });
      toast.error(getApiErrorMessage(error, 'products.loadDetails.loadError'));
    } finally {
      setLoading(false);
    }
  }, [product?.id, toast]);

  const goToPage = useCallback((page) => {
    fetchLoads({ page });
  }, [fetchLoads]);

  // Fetch filter options
  const { data: clients } = usePaginatedApi(clientsAPI.getAll, { limit: 100 });
  const { data: trucks } = usePaginatedApi(trucksAPI.getAll, { limit: 100 });
  const { data: ports } = usePaginatedApi(portsAPI.getAll, { limit: 100 });
  const { data: weighbridges } = usePaginatedApi(weighbridgesAPI.getAll, { limit: 100 });

  // Reset filters and fetch data when modal opens
  useEffect(() => {
    if (isOpen && product?.id) {
      const resetFilters = {
        clientId: '',
        truckId: '',
        portId: '',
        weighbridgeId: '',
        startDate: '',
        endDate: '',
        transactionType: '',
        status: ''
      };
      setFilters(resetFilters);
      setShowFilters(false);
      
      // Fetch loads directly without using the callback to avoid circular dependency
      const fetchData = async () => {
        if (!product?.id) return;
        
        try {
          setLoading(true);
          const response = await productsAPI.getLoads(product.id, resetFilters);
          
          setLoads(response.data.loads || []);
          setPagination(response.data.pagination || {});
        } catch (error) {
          console.error('Error fetching product loads:', error);
          setLoads([]);
          setPagination({ total: 0, page: 1, limit: 20, totalPages: 0 });
          toast.error(getApiErrorMessage(error, 'products.loadDetails.loadError'));
        } finally {
          setLoading(false);
        }
      };
      
      fetchData();
    }
  }, [isOpen, product?.id]);

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
  };

  const applyFilters = () => {
    fetchLoads({ page: 1 });
  };

  const resetFilters = () => {
    const emptyFilters = {
      clientId: '',
      truckId: '',
      portId: '',
      weighbridgeId: '',
      startDate: '',
      endDate: '',
      transactionType: '',
      status: ''
    };
    setFilters(emptyFilters);
    // Use setTimeout to ensure filters are updated before fetch
    setTimeout(() => {
      fetchLoads({ page: 1 });
    }, 0);
  };

  const exportToCSV = () => {
    if (!loads || loads.length === 0) return;

    const headers = [
      t('products.loadDetails.date'),
      t('products.loadDetails.client'),
      t('products.loadDetails.truck'),
      t('products.loadDetails.driver'),
      t('products.loadDetails.port'),
      t('products.loadDetails.weighbridge'),
      t('products.loadDetails.transactionType'),
      t('products.loadDetails.netWeight'),
      t('products.loadDetails.status'),
      t('products.loadDetails.ticketNumber')
    ];

    const csvContent = [
      headers.join(','),
      ...loads.map(load => [
        `"${formatDateTime(load.loadDate)}"`,
        `"${load.client?.companyName || t('common.notAssigned')}"`,
        `"${load.truck?.plateNumber || t('common.notAssigned')}"`,
        `"${load.truck?.driverName || t('common.notAssigned')}"`,
        `"${load.port?.name || t('common.notAssigned')}"`,
        `"${load.weighbridge?.name || t('common.notAssigned')}"`,
        `"${load.transactionType || t('common.notAssigned')}"`,
        `"${formatWeight(load.netWeight, 'ton', { noUnit: true })}"`,
        `"${t(`loads.statuses.${load.status?.toLowerCase()}`)}"`,
        `"${load.ticket?.ticketNumber || t('common.notGenerated')}"`
      ].join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `product-${product?.name}-loads-${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const getStatusBadgeColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'requested': return 'bg-blue-100 text-blue-800';
      case 'accepted': return 'bg-yellow-100 text-yellow-800';
      case 'loaded': return 'bg-green-100 text-green-800';
      case 'delivered': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const columns = [
    {
      key: 'loadDate',
      title: t('products.loadDetails.date'),
      sortable: true,
      render: (value) => formatDateTime(value),
    },
    {
      key: 'client',
      title: t('products.loadDetails.client'),
      render: (value, row) => (
        <div>
          <div className="font-medium text-gray-900">{row.client?.companyName || t('common.notAssigned')}</div>
          {row.client?.contactName && (
            <div className="text-xs text-gray-500">{row.client.contactName}</div>
          )}
        </div>
      ),
    },
    {
      key: 'truck',
      title: t('products.loadDetails.truck'),
      render: (value, row) => (
        <div>
          <div className="font-medium text-gray-900">{row.truck?.plateNumber || t('common.notAssigned')}</div>
          {row.truck?.driverName && (
            <div className="text-xs text-gray-500">{row.truck.driverName}</div>
          )}
        </div>
      ),
    },
    {
      key: 'port',
      title: t('products.loadDetails.port'),
      render: (value, row) => (
        <div>
          <div className="font-medium text-gray-900">{row.port?.name || t('common.notAssigned')}</div>
          {row.port?.location && (
            <div className="text-xs text-gray-500">{row.port.location}</div>
          )}
        </div>
      ),
    },
    {
      key: 'weighbridge',
      title: t('products.loadDetails.weighbridge'),
      render: (value, row) => (
        <div>
          <div className="font-medium text-gray-900">{row.weighbridge?.name || t('common.notAssigned')}</div>
          {row.weighbridge?.location && (
            <div className="text-xs text-gray-500">{row.weighbridge.location}</div>
          )}
        </div>
      ),
    },
    {
      key: 'transactionType',
      title: t('products.loadDetails.transactionType'),
      render: (value) => (
        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
          value === 'IMPORT' ? 'bg-blue-100 text-blue-800' : 'bg-green-100 text-green-800'
        }`}>
          {value === 'IMPORT' ? t('loads.transactionTypes.import') : t('loads.transactionTypes.export')}
        </span>
      ),
    },
    {
      key: 'netWeight',
      title: t('products.loadDetails.netWeight'),
      align: 'right',
      render: (value) => (
        <span className="font-semibold text-blue-600">
          {formatWeight(value, 'ton', { noUnit: true })}
        </span>
      ),
    },
    {
      key: 'status',
      title: t('products.loadDetails.status'),
      render: (value) => (
        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusBadgeColor(value)}`}>
          {t(`loads.statuses.${value?.toLowerCase()}`)}
        </span>
      ),
    },
  ];

  if (!product) return null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={t('products.loadDetails.title', { product: product.name })}
      size="full"
      footer={
        <div className="flex items-center justify-between w-full">
          <div className="flex items-center space-x-2">
            <Button
              variant="outline"
              onClick={exportToCSV}
              disabled={!loads || loads.length === 0}
              className="flex items-center"
            >
              <DocumentArrowDownIcon className="h-4 w-4 mr-1" />
              {t('common.exportCSV')}
            </Button>
          </div>
          <Button variant="outline" onClick={onClose}>
            {t('common.close')}
          </Button>
        </div>
      }
    >
      <div className="space-y-6">
        {/* Filters Section - Always at the top */}
        <div className="bg-white border border-gray-200 rounded-lg shadow-sm">
          {/* Filter Header */}
          <div className="bg-gray-50 px-4 py-3 border-b border-gray-200 rounded-t-lg">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <FunnelIcon className="h-5 w-5 text-gray-600" />
                <h3 className="text-sm font-medium text-gray-900">{t('loads.filters')}</h3>
                {/* Active filter count indicator */}
                {(() => {
                  const activeFilters = Object.values(filters).filter(value => value && value.trim() !== '').length;
                  return activeFilters > 0 ? (
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                      {activeFilters} {activeFilters === 1 ? 'filter' : 'filters'} active
                    </span>
                  ) : null;
                })()}
              </div>
              <div className="flex items-center space-x-2">
                {Object.values(filters).some(value => value && value.trim() !== '') && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={resetFilters}
                    className="text-blue-600 hover:text-blue-700 text-xs"
                  >
                    Clear all
                  </Button>
                )}
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowFilters(!showFilters)}
                  className="text-gray-600 hover:text-gray-900"
                >
                  {showFilters ? t('common.hideFilters') : t('common.showFilters')}
                </Button>
              </div>
            </div>
          </div>

          {/* Filter Content */}
          {showFilters && (
            <div className="p-4">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {/* Client Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <BuildingOfficeIcon className="h-4 w-4 inline mr-1" />
                  {t('products.loadDetails.filterByClient')}
                </label>
                <select
                  value={filters.clientId}
                  onChange={(e) => handleFilterChange('clientId', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">{t('common.allClients')}</option>
                  {clients?.map(client => (
                    <option key={client.id} value={client.id}>
                      {client.companyName}
                    </option>
                  ))}
                </select>
              </div>

              {/* Truck Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <TruckIcon className="h-4 w-4 inline mr-1" />
                  {t('products.loadDetails.filterByTruck')}
                </label>
                <select
                  value={filters.truckId}
                  onChange={(e) => handleFilterChange('truckId', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">{t('common.allTrucks')}</option>
                  {trucks?.map(truck => (
                    <option key={truck.id} value={truck.id}>
                      {truck.plateNumber} - {truck.driverName}
                    </option>
                  ))}
                </select>
              </div>

              {/* Port Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <MapPinIcon className="h-4 w-4 inline mr-1" />
                  {t('products.loadDetails.filterByPort')}
                </label>
                <select
                  value={filters.portId}
                  onChange={(e) => handleFilterChange('portId', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">{t('common.allPorts')}</option>
                  {ports?.map(port => (
                    <option key={port.id} value={port.id}>
                      {port.name} - {port.location}
                    </option>
                  ))}
                </select>
              </div>

              {/* Weighbridge Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <ScaleIcon className="h-4 w-4 inline mr-1" />
                  {t('products.loadDetails.filterByWeighbridge')}
                </label>
                <select
                  value={filters.weighbridgeId}
                  onChange={(e) => handleFilterChange('weighbridgeId', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">{t('common.allWeighbridges')}</option>
                  {weighbridges?.map(weighbridge => (
                    <option key={weighbridge.id} value={weighbridge.id}>
                      {weighbridge.name} - {weighbridge.location}
                    </option>
                  ))}
                </select>
              </div>

              {/* Date Range */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <CalendarIcon className="h-4 w-4 inline mr-1" />
                  {t('products.loadDetails.startDate')}
                </label>
                <Input
                  type="date"
                  value={filters.startDate}
                  onChange={(e) => handleFilterChange('startDate', e.target.value)}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <CalendarIcon className="h-4 w-4 inline mr-1" />
                  {t('products.loadDetails.endDate')}
                </label>
                <Input
                  type="date"
                  value={filters.endDate}
                  onChange={(e) => handleFilterChange('endDate', e.target.value)}
                />
              </div>

              {/* Transaction Type Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('products.loadDetails.transactionType')}
                </label>
                <select
                  value={filters.transactionType}
                  onChange={(e) => handleFilterChange('transactionType', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">{t('common.allTypes')}</option>
                  <option value="IMPORT">{t('loads.transactionTypes.import')}</option>
                  <option value="EXPORT">{t('loads.transactionTypes.export')}</option>
                </select>
              </div>

              {/* Status Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('products.loadDetails.status')}
                </label>
                <select
                  value={filters.status}
                  onChange={(e) => handleFilterChange('status', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">{t('common.allStatuses')}</option>
                  <option value="REQUESTED">{t('loads.statuses.requested')}</option>
                  <option value="ACCEPTED">{t('loads.statuses.accepted')}</option>
                  <option value="LOADED">{t('loads.statuses.loaded')}</option>
                  <option value="DELIVERED">{t('loads.statuses.delivered')}</option>
                </select>
              </div>
            </div>

              <div className="flex items-center space-x-2 mt-4">
                <Button onClick={applyFilters}>
                  {t('common.applyFilters')}
                </Button>
                <Button variant="outline" onClick={resetFilters}>
                  {t('common.resetFilters')}
                </Button>
              </div>
            </div>
          )}
        </div>

        {/* Product Info */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-center space-x-3">
            <div className="flex-shrink-0">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                <TruckIcon className="h-6 w-6 text-blue-600" />
              </div>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">{product.name}</h3>
              <p className="text-sm text-gray-600">
                {t('products.loadDetails.totalLoads', { count: pagination?.total || 0 })}
              </p>
            </div>
          </div>
        </div>

        {/* Loads Table */}
        <div className="card">
          <Table
            columns={columns}
            data={loads}
            loading={loading}
            pagination={pagination}
            onPageChange={goToPage}
            emptyMessage={t('products.loadDetails.noLoadsFound')}
          />
        </div>
      </div>
    </Modal>
  );
};

export default ProductLoadsModal;
